import { toBnDigits } from '../../../../../utils/bnFormat';
import {
    HIJRI_MONTHS_AR, HIJRI_MONTHS_BN, HIJRI_MONTHS_EN,
    toBanglaDate, toHijriDate,
} from '../../../../../utils/banglaCalendar';

const EN_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const BN_MONTHS_GREG = [
    'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
    'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর',
];

function pad(n) {
    return String(n).padStart(2, '0');
}

function ordinal(n) {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = n % 100;
    return `${n}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
}

export function todayInputValue() {
    const today = new Date();
    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

export function parseDateInput(value) {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
}

function gregorianToBangla(gYear, gMonth, gDay) {
    return toBanglaDate(new Date(gYear, gMonth - 1, gDay));
}

function gregorianToHijri(year, month, day) {
    return toHijriDate(new Date(year, month - 1, day));
}

function getArabicFormatted(year, month, day) {
    try {
        return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(new Date(year, month - 1, day));
    } catch {
        return null;
    }
}

export function getGregorianFormats(parsed) {
    const { year, month, day } = parsed;
    const dd = pad(day);
    const mm = pad(month);
    const yyyy = String(year);
    const monthEn = EN_MONTHS[month - 1];
    const monthBn = BN_MONTHS_GREG[month - 1];

    return [
        { id: 'ddmmyyyySlash', value: `${dd}/${mm}/${yyyy}` },
        { id: 'ddmmyyyyDash', value: `${dd}-${mm}-${yyyy}` },
        { id: 'iso8601', value: `${yyyy}-${mm}-${dd}` },
        { id: 'mmddyyyy', value: `${mm}/${dd}/${yyyy}` },
        { id: 'ordinalEnglish', value: `${ordinal(day)} ${monthEn} ${yyyy}` },
        { id: 'monthDayYear', value: `${monthEn} ${day}, ${yyyy}` },
        { id: 'ddmmyyyyBnDigits', value: toBnDigits(`${dd}/${mm}/${yyyy}`) },
        { id: 'fullBnGregorian', value: `${toBnDigits(day)} ${monthBn}, ${toBnDigits(yyyy)} খ্রিস্টাব্দ` },
    ];
}

export function getBanglaFormats(parsed) {
    const bangla = gregorianToBangla(parsed.year, parsed.month, parsed.day);
    if (!bangla) return [];
    return [
        { id: 'short', value: `${toBnDigits(bangla.day)} ${bangla.monthBn} ${toBnDigits(bangla.year)}` },
        { id: 'fullBn', value: `${toBnDigits(bangla.day)} ${bangla.monthBn}, ${toBnDigits(bangla.year)} বঙ্গাব্দ` },
        { id: 'englishBs', value: `${bangla.day} ${bangla.monthEn} ${bangla.year} BS` },
        { id: 'numeric', value: `${toBnDigits(pad(bangla.day))}/${toBnDigits(pad(bangla.month))}/${toBnDigits(bangla.year)}` },
    ];
}

export function getHijriFormats(parsed) {
    const hijri = gregorianToHijri(parsed.year, parsed.month, parsed.day);
    if (!hijri) return [];

    const arabic = getArabicFormatted(parsed.year, parsed.month, parsed.day);
    return [
        {
            id: 'arabic',
            value: arabic || `${hijri.day} ${HIJRI_MONTHS_AR[hijri.month - 1]} ${hijri.year} هـ`,
        },
        {
            id: 'bengaliHijri',
            value: `${toBnDigits(hijri.day)} ${HIJRI_MONTHS_BN[hijri.month - 1]}, ${toBnDigits(hijri.year)} হিজরি`,
        },
        { id: 'englishAh', value: `${hijri.day} ${HIJRI_MONTHS_EN[hijri.month - 1]} ${hijri.year} AH` },
        { id: 'numeric', value: `${pad(hijri.day)}/${pad(hijri.month)}/${hijri.year}` },
    ];
}

export function getAllDateFormats(dateInput) {
    const parsed = parseDateInput(dateInput);
    if (!parsed) {
        return { parsed: null, gregorian: [], bangla: [], hijri: [] };
    }
    return {
        parsed,
        gregorian: getGregorianFormats(parsed),
        bangla: getBanglaFormats(parsed),
        hijri: getHijriFormats(parsed),
    };
}
